import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Call } from "@mui/icons-material";
import axios from "axios";
import { useState } from "react";
import PhoneInput from "react-phone-input-2";
import logo from "../../assets/logo.png";

// Agents created by backend/expatinsure-demo/scripts/setup_expatinsure_demo.py
// (ids persisted in backend/expatinsure-demo/config/expatinsure_demo_config.json).
export const FROM_NUMBER = "12392081459"; // US (239) Retell-Twilio number
export const BASE_URL =
  "https://initiateretellcall-655075094783.us-central1.run.app";
export const ADVISOR_NAME = "Louis";

// Next two weekdays after `now`, as the bookable advisor slots the agent offers.
export const nextBusinessSlots = (now = new Date()) => {
  const days = [];
  const d = new Date(now);
  while (days.length < 2) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(new Date(d));
  }
  const name = (day) => day.toLocaleDateString("en-US", { weekday: "long" });
  return {
    slot_1: `${name(days[0])} at 11 a.m. Eastern`,
    slot_2: `${name(days[1])} at 3 p.m. Eastern`,
  };
};

export const AGENTS = {
  speed_to_lead: {
    label: "Sarah — new website lead (speed-to-lead)",
    agentName: "Sarah",
    agentId: "agent_50ca42a4b18a1d25d29cd4dd85",
    variables: {},
    scenarios: {
      advisor_live: {
        label: "Advisor available — live transfer",
        description:
          "Sarah asks four quick questions (destination, start date, who's covered, budget), then transfers you straight to the advisor number below.",
        advisorAvailable: "yes",
      },
      advisor_busy: {
        label: "Advisor busy — qualify and book",
        description:
          "No advisor free: after the four quick questions Sarah takes your email, DOB and nationality and books a call.",
        advisorAvailable: "no",
      },
      no_meeting: {
        label: "“I don't want a meeting”",
        description:
          "Push back on booking a call. Some ideas:",
        chips: [
          "I don't want a meeting",
          "Just email me the quotes",
          "How much does it cost?",
          "Can I just talk to someone now?",
          "I'm moving in 2028",
          "My budget is $100 a month",
          "Are you a robot?",
        ],
        advisorAvailable: "no",
      },
      screener: {
        label: "Call screener answers first",
        description:
          "Play the phone's screening assistant before you “pick up”. Try:",
        chips: [
          "Please say your name and why you're calling",
          "Press 1 to be connected",
          "What is 3 plus 6? Enter it on your keypad",
        ],
        advisorAvailable: "no",
      },
      voicemail: {
        label: "Voicemail",
        description:
          "Answer like a voicemail greeting (“You've reached… leave a message”) and hear the message Sarah leaves.",
        advisorAvailable: "no",
      },
    },
  },
  quote_followup: {
    label: "Mary — quote follow-up",
    agentName: "Mary",
    agentId: "agent_a01caa72105fd10e474a6c5bf8",
    variables: {
      quote_sent: "last week",
      destination_country: "Portugal",
      callback_number: "the office number on your quote email",
    },
    scenarios: {
      interested: {
        label: "Interested — book the broker",
        description: "You liked the quote. Mary confirms start date, books a broker call and checks your number.",
      },
      needs_time: {
        label: "Need more time",
        description: "You're still thinking. Mary pins down a rough start date and a specific check-back time.",
      },
      too_expensive: {
        label: "Too expensive",
        description: "Say the quote was too pricey. Mary offers lower-cost options with the broker (deductibles, other insurers).",
      },
      not_interested: {
        label: "Not interested / bought elsewhere",
        description: "Turn it down. Mary asks to keep your details on file and wraps up politely.",
      },
      voicemail: {
        label: "Voicemail",
        description: "Answer like a voicemail greeting and hear the message Mary leaves.",
      },
    },
  },
};

const whiteField = { backgroundColor: "white", borderRadius: "4px" };
const phoneStyle = {
  width: "100%",
  textAlign: "center",
  fontSize: "16px",
  height: "56px",
  borderRadius: "4px",
  border: "1px solid #ced4da",
};

export const ExpatInsureDemo = () => {
  const [agentKey, setAgentKey] = useState("speed_to_lead");
  const [scenario, setScenario] = useState("advisor_busy");
  const [contactName, setContactName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [advisorNumber, setAdvisorNumber] = useState("");
  const [status, setStatus] = useState(null); // null | "loading" | "success" | "error"

  const agent = AGENTS[agentKey];
  const current = agent.scenarios[scenario];
  const needsAdvisor = current.advisorAvailable === "yes";

  const changeAgent = (key) => {
    setAgentKey(key);
    setScenario(Object.keys(AGENTS[key].scenarios)[0]);
    setStatus(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("loading");

    const params = {
      agent_id: agent.agentId,
      from_number: FROM_NUMBER,
      to_number: phoneNumber,
      scenario,
      contact_name: contactName.trim(),
      advisor_name: ADVISOR_NAME,
      ...nextBusinessSlots(),
      ...agent.variables,
    };
    if (agentKey === "speed_to_lead") {
      params.advisor_available = current.advisorAvailable;
      params.advisor_number = needsAdvisor ? `+${advisorNumber}` : "";
    }

    try {
      await axios.get(BASE_URL, { params });
      setStatus("success");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  return (
    <div className="full-page-background">
      <Container className="App" maxWidth="sm">
        <img src={logo} className="App-logo" alt="logo" />
        <h1>ExpatInsure — AI Calling Assistant</h1>
        <h3>
          Demo by JasperVOCAL · Pick an agent and a scenario, enter your number,
          and {agent.agentName} will call you.
        </h3>

        <form onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal">
            <Select
              value={agentKey}
              onChange={(e) => changeAgent(e.target.value)}
              inputProps={{ "aria-label": "Agent" }}
              sx={whiteField}
            >
              {Object.entries(AGENTS).map(([key, a]) => (
                <MenuItem key={key} value={key}>
                  {a.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <Select
              value={scenario}
              onChange={(e) => {
                setScenario(e.target.value);
                setStatus(null);
              }}
              inputProps={{ "aria-label": "Scenario" }}
              sx={whiteField}
            >
              {Object.entries(agent.scenarios).map(([key, s]) => (
                <MenuItem key={key} value={key}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.8)", mb: 1, mt: 0.5, fontStyle: "italic" }}
          >
            {current.description}
          </Typography>

          {current.chips && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center" sx={{ mb: 1 }}>
              {current.chips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  size="small"
                  variant="outlined"
                  sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)" }}
                />
              ))}
            </Stack>
          )}

          <TextField
            label="Your first name"
            variant="filled"
            sx={whiteField}
            fullWidth
            margin="normal"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            required
          />

          <Box sx={{ mt: 2 }}>
            <PhoneInput
              country="us"
              preferredCountries={["us", "gb", "ca"]}
              value={phoneNumber}
              onChange={setPhoneNumber}
              enableLongNumbers={true}
              inputProps={{ required: true, "aria-label": "Your phone number" }}
              inputStyle={phoneStyle}
            />
          </Box>

          {needsAdvisor && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", mb: 1 }}>
                Advisor's phone (the live transfer rings this number):
              </Typography>
              <PhoneInput
                country="us"
                preferredCountries={["us", "gb", "ca"]}
                value={advisorNumber}
                onChange={setAdvisorNumber}
                enableLongNumbers={true}
                inputProps={{ required: true, "aria-label": "Advisor phone number" }}
                inputStyle={phoneStyle}
              />
            </Box>
          )}

          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", mt: 2, mb: 2 }}>
            You will receive a call from: <b>+1 (239) 208-1459</b>
          </Typography>

          {status === "success" && (
            <Typography sx={{ color: "#4caf50", mb: 1, fontWeight: "bold" }}>
              ✓ {agent.agentName} is calling you now. Pick up and play the scenario.
            </Typography>
          )}
          {status === "error" && (
            <Typography sx={{ color: "#f44336", mb: 1, fontWeight: "bold" }}>
              ✗ Could not start the call. Please try again.
            </Typography>
          )}

          <Button
            className="callBtn"
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={status === "loading"}
          >
            <h2 style={{ fontSize: "16px" }}>{status === "loading" ? "Calling…" : "Call me now"}</h2>
            <div style={{ width: "20px" }} />
            <Call className="callIcon" />
          </Button>
        </form>

        <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.7)", mt: 3 }}>
          Every call ends with a structured summary (outcome, qualification answers, booked slot). In
          production these sync to Salesforce and bookings land in your Calendly.
        </Typography>
      </Container>
    </div>
  );
};
